import { FC, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { StoreApi, UseBoundStore } from 'zustand'

import { Toast } from '@/components/ui/toast'

import { DEFAULT_DURATION } from './create-toast-factory.constants'
import * as S from './create-toast-factory.styled'
import { ToastStoreState } from './create-toast-factory.types'

export type ToastProviderProps = {
  useToastStore: UseBoundStore<StoreApi<ToastStoreState>>
  mountProvider: () => () => void
  dismiss: (id: string) => void
}

export const ToastProvider: FC<ToastProviderProps> = ({
  useToastStore,
  mountProvider,
  dismiss,
}) => {
  const { toasts } = useToastStore()
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const startTimesRef = useRef<Map<string, number>>(new Map())
  const remainingTimesRef = useRef<Map<string, number>>(new Map())
  // Note: the `shownVersion` each timer was started for - a newer one means the toast was shown again
  const shownVersionRef = useRef<Map<string, number>>(new Map())
  // Note: toasts under the pointer stay paused, even when other toasts come and go meanwhile
  const hoveredRef = useRef<Set<string>>(new Set())

  useEffect(mountProvider, [mountProvider])

  const handleDismiss = useCallback(
    (id: string) => {
      const timer = timersRef.current.get(id)
      if (timer) {
        clearTimeout(timer)
        timersRef.current.delete(id)
      }
      startTimesRef.current.delete(id)
      remainingTimesRef.current.delete(id)
      shownVersionRef.current.delete(id)
      hoveredRef.current.delete(id)
      dismiss(id)
    },
    [dismiss],
  )

  const handleMouseEnter = useCallback((id: string) => {
    hoveredRef.current.add(id)
    const timer = timersRef.current.get(id)
    const startTime = startTimesRef.current.get(id)

    if (timer && startTime) {
      clearTimeout(timer)
      timersRef.current.delete(id)

      const elapsed = Date.now() - startTime
      const remaining = (remainingTimesRef.current.get(id) || DEFAULT_DURATION) - elapsed
      remainingTimesRef.current.set(id, Math.max(remaining, 0))
    }
  }, [])

  const handleMouseLeave = useCallback(
    (id: string) => {
      hoveredRef.current.delete(id)
      const remaining = remainingTimesRef.current.get(id)

      if (remaining && remaining > 0 && !timersRef.current.has(id)) {
        startTimesRef.current.set(id, Date.now())
        const timer = setTimeout(() => {
          handleDismiss(id)
        }, remaining)
        timersRef.current.set(id, timer)
      }
    },
    [handleDismiss],
  )

  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.isExiting || !toast.duration || toast.duration <= 0) return

      // Note: shown again - the full duration starts over
      if (shownVersionRef.current.get(toast.id) !== toast.shownVersion) {
        shownVersionRef.current.set(toast.id, toast.shownVersion)
        clearTimeout(timersRef.current.get(toast.id))
        timersRef.current.delete(toast.id)
        remainingTimesRef.current.set(toast.id, toast.duration)
      }

      if (!timersRef.current.has(toast.id) && !hoveredRef.current.has(toast.id)) {
        startTimesRef.current.set(toast.id, Date.now())
        const timer = setTimeout(() => {
          handleDismiss(toast.id)
        }, remainingTimesRef.current.get(toast.id))
        timersRef.current.set(toast.id, timer)
      }
    })

    // Note: Cleanup timers for toasts that no longer exist
    timersRef.current.forEach((timer, id) => {
      if (!toasts.find((t) => t.id === id)) {
        clearTimeout(timer)
        timersRef.current.delete(id)
        startTimesRef.current.delete(id)
        remainingTimesRef.current.delete(id)
      }
    })
    shownVersionRef.current.forEach((_, id) => {
      if (!toasts.find((t) => t.id === id)) {
        shownVersionRef.current.delete(id)
        hoveredRef.current.delete(id)
      }
    })
  }, [toasts, handleDismiss])

  // Note: Cleanup all timers on component unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer))
      timersRef.current.clear()
      startTimesRef.current.clear()
      remainingTimesRef.current.clear()
      shownVersionRef.current.clear()
      hoveredRef.current.clear()
    }
  }, [])

  if (toasts.length === 0) return null

  return createPortal(
    <S.ToastContainer>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          description={toast.description}
          isExiting={toast.isExiting}
          onClose={() => handleDismiss(toast.id)}
          onMouseEnter={() => handleMouseEnter(toast.id)}
          onMouseLeave={() => handleMouseLeave(toast.id)}
        />
      ))}
    </S.ToastContainer>,
    document.body,
  )
}
